import {
  ActionList,
  ActionListItem,
  BasicButton,
  ButtonGroup,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Divider,
  FieldErrors,
  GhostIconButton,
  Modal,
  ModalTrigger,
  OuterContentItem,
  PrimaryButton,
  RequirementsList,
  SubHeading,
} from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import {
  ArrayControlProps,
  ControlElement,
  Helpers,
  Layout,
  RuleEffect,
  composePaths,
  findUISchema,
  update,
} from "@jsonforms/core";
import {
  JsonFormsDispatch,
  useJsonForms,
  withJsonFormsArrayControlProps,
} from "@jsonforms/react";
import get from "lodash/get";
import range from "lodash/range";
import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormValidationContext, FormProps } from "react-aria-components";
import { Trans, useTranslation } from "react-i18next";

import {
  convertDataToDisplayValues,
  getItemList,
  getRequirementsList,
  getScopeLookupPath,
  splitAndCapitalize,
  withArrayControlVisibility,
} from "./utils";
import {
  CascadiaJsonForm,
  CascadiaJsonFormContent,
  CascadiaJsonFormSubmit,
} from "../CascadiaJsonForm";
import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { Link } from "../components/Link";
import { Text } from "../components/Text";
import { CascadiaFormSubmissionError } from "../errors";

type IsMultiPageWhen = {
  field: string;
  const: unknown;
};

export function getEffectiveLastPageIdx(
  lastPageIdx: number,
  isMultiPageWhen: IsMultiPageWhen | undefined,
  values: Record<string, unknown> | undefined
): number {
  if (!isMultiPageWhen || values == null) {
    return lastPageIdx;
  }

  if (values[isMultiPageWhen.field] === isMultiPageWhen.const) {
    return 0;
  }

  return lastPageIdx;
}

function ActionListMultiPageFooterButtons({
  itemName,
  isMultiPage,
  isNextPageDisabled,
  currentPageIdx,
  lastPageIdx,
  isMultiPageWhen,
  setCurrentPageIdx,
}: {
  itemName: string;
  isMultiPage: boolean | undefined;
  currentPageIdx: number;
  isNextPageDisabled?: (values: Record<string, unknown> | undefined) => boolean;
  lastPageIdx: number;
  isMultiPageWhen?: IsMultiPageWhen;
  setCurrentPageIdx: (idx: number) => void;
}) {
  const { values } = useContext(CascadiaJsonFormContext);
  const effectiveLastPageIdx = getEffectiveLastPageIdx(
    lastPageIdx,
    isMultiPageWhen,
    values as Record<string, unknown> | undefined
  );
  const isDisabled =
    isNextPageDisabled?.(values as Record<string, unknown> | undefined) ??
    false;

  if (isMultiPage && currentPageIdx < effectiveLastPageIdx) {
    return (
      <PrimaryButton
        testID={`${slugify(itemName.toLowerCase())}-next-button`}
        onPress={() => setCurrentPageIdx(currentPageIdx + 1)}
        isDisabled={isDisabled}
      >
        <Trans>Next</Trans>
      </PrimaryButton>
    );
  }

  return (
    <CascadiaJsonFormSubmit
      testId={slugify(itemName.toLowerCase())}
      isDisabled={isDisabled}
    >
      <Trans>Save</Trans>
    </CascadiaJsonFormSubmit>
  );
}

interface Props extends ArrayControlProps {
  filteredTotal?: number;
  /**
   * If set to true, the requirements will be displayed as bullet points regardless of the Control's current validity.
   */
  alwaysDisplayRequirementsAsBullets?: boolean;
  /**
   * When true, FieldErrors are not rendered (e.g. when the control shows errors via its own requirements list).
   */
  hideFieldErrors?: boolean;
  renderTag?: (item: any) => React.ReactNode;
  renderSubtitle?: (item: any) => React.ReactNode;
  renderTitle?: (item: any) => React.ReactNode;
  onItemRemoved?: (parentValues: object[], indexes: number[]) => void;
  onItemChanged?: (
    parentValues: object[],
    values: object,
    index: number
  ) => Promise<void | FormProps["validationErrors"]>;
  getItemList?: (labels: string[], data: Record<string, unknown>) => string[];
  renderers?: ArrayControlProps["renderers"];
  isNextPageDisabled?: (values: Record<string, unknown> | undefined) => boolean;
}

export function ActionListControl(props: Props) {
  const { t } = useTranslation();
  const { dispatch } = useJsonForms();
  const {
    isSummary,
    isSubmitted,
    locale,
    values: rootValues,
    formID,
  } = useContext(CascadiaJsonFormContext);

  const {
    data,
    path,
    schema,
    removeItems,
    addItem,
    uischema,
    uischemas,
    rootSchema,
    filteredTotal,
    alwaysDisplayRequirementsAsBullets,
    isNextPageDisabled,
  } = props;

  const [currentPageIdx, setCurrentPageIdx] = useState(0);
  const modalContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    modalContentRef.current?.scrollTo({ top: 0 });
  }, [currentPageIdx]);

  const formValidation = useContext(FormValidationContext);

  const label =
    props.uischema.options?.fieldErrorLabel || props.label || t("Field");

  const errors = useMemo(() => {
    let errors: string[] = [];
    const ajvErrors = props.errors.split("\n");
    const submitErrors = formValidation[props.path];

    if (ajvErrors.length > 0) {
      errors = errors.concat(
        ajvErrors.filter(Boolean).map((err) => [label, err].join(" "))
      );
    }

    if ((props.data === undefined || props.data === "") && props.required) {
      errors.push(t("{{label}} is a required field", { label }));
    }

    if (Array.isArray(submitErrors)) {
      errors = errors.concat(submitErrors);
    } else if (typeof submitErrors === "string") {
      errors.push(submitErrors);
    }

    return [...new Set(errors)];
  }, [props.data, props.errors, formValidation[props.path]]);

  const itemName = uischema.options?.itemName || label;

  const childUiSchema = useMemo(
    () =>
      findUISchema(
        uischemas || [],
        schema,
        uischema.scope,
        path,
        undefined,
        uischema,
        rootSchema
      ),
    [uischemas, schema, uischema.scope, path, uischema, rootSchema]
  );

  let uiSchemaToUse = (childUiSchema || uischema) as Layout;
  const isMultiPage = uischema.options?.isMultiPage;
  const isMultiPageWhen = uischema.options?.isMultiPageWhen as
    | IsMultiPageWhen
    | undefined;
  const lastPageIdx = (uiSchemaToUse.elements?.length || 1) - 1;
  if (isMultiPage) {
    uiSchemaToUse = {
      ...uiSchemaToUse,
      elements: uiSchemaToUse?.elements.map((element, idx) => ({
        ...element,
        rule: {
          effect: idx === currentPageIdx ? RuleEffect.SHOW : RuleEffect.HIDE,
          condition: {
            scope: "#",
            schema: {},
            type: "SchemaBasedCondition",
          },
        },
      })),
    };
  }

  if (isSummary) {
    return (
      <>
        {data?.map((item: Record<string, unknown>, i: number) => {
          const childPath = composePaths(path, `${i}`);

          const itemDisplayValues = convertDataToDisplayValues(
            item,
            props.schema
          );

          const title = (props.getItemList || getItemList)(
            [uischema.options?.elementLabel ?? Object.keys(item)[0]],
            itemDisplayValues
          ).filter(Boolean)[0];

          const subtitles = (props.getItemList || getItemList)(
            uischema.options?.elementSubLabels ?? Object.keys(item),
            itemDisplayValues
          )
            .filter(Boolean)
            .join(", ");

          return (
            <Fragment key={childPath}>
              {isSummary && <SubHeading children={props.label} />}
              <OuterContentItem
                title={splitAndCapitalize(title)}
                subtitle={subtitles === "undefined" ? undefined : subtitles}
                key={i}
                testID={`${props.path}-summary-item-outer-content`}
              >
                <JsonFormsDispatch
                  schema={schema}
                  uischema={uiSchemaToUse}
                  path={childPath}
                  key={childPath}
                />
              </OuterContentItem>
            </Fragment>
          );
        })}
      </>
    );
  }

  const requirementMessages = get(
    rootSchema,
    getScopeLookupPath(uischema.scope)
  )?.requirements?.filter(
    (msg: string) =>
      !msg.toLowerCase().includes("postcode") ||
      (Array.isArray(data) &&
        data.some(
          (item: { address?: { country?: string } }) =>
            item?.address?.country === "GB"
        ))
  );

  const validationMaxItems = get(
    rootSchema,
    getScopeLookupPath(uischema.scope)
  )?.maxItems;

  // if maxItem validation exists, we'll default to filteredTotal (filter
  // for things such as do not contact), otherwise just use data size
  const hasMaximumValidationItems =
    validationMaxItems &&
    (filteredTotal || data?.length) === validationMaxItems;

  const maxItems = uischema.options?.maxItems ?? validationMaxItems;

  function navigateToFirstErrorPage(errors: object) {
    if (isMultiPage) {
      const firstErrorPageIdx = Object.keys(errors).reduce(
        (firstErrorPageIdx, key) =>
          Math.min(
            firstErrorPageIdx,
            (uiSchemaToUse.elements || []).findIndex((page: any) =>
              (page.elements || []).some((element: any) => element.name === key)
            )
          ),
        lastPageIdx
      );

      setCurrentPageIdx(firstErrorPageIdx);
    }
  }

  return (
    <div className={styles.arrayRenderer}>
      {uischema?.options?.showTopDivider && <Divider />}
      {uischema?.options?.description && (
        <Text style="input/label">{uischema?.options?.description}</Text>
      )}
      {data?.length > 0 && (
        <div className={styles.array}>
          <ActionList>
            {range(0, data?.length).map((index) => {
              const item = props.data[index] || {};
              const itemDisplayValues = convertDataToDisplayValues(
                item,
                props.schema
              );

              const title = (props.getItemList || getItemList)(
                [uischema.options?.elementLabel ?? Object.keys(item)[0]],
                itemDisplayValues
              ).filter(Boolean)[0];

              const subtitles = (props.getItemList || getItemList)(
                uischema.options?.elementSubLabels ?? Object.keys(item),
                itemDisplayValues
              ).filter(Boolean);

              return (
                <Fragment key={index}>
                  <ActionListItem
                    tag={props.renderTag ? props.renderTag(item) : undefined}
                    title={props.renderTitle ? props.renderTitle(item) : title}
                    trailing={
                      <ModalTrigger>
                        <Link
                          color="text-weak"
                          testID={`${slugify(
                            itemName.toLowerCase()
                          )}-edit-link`}
                        >
                          <Trans>Edit</Trans>
                        </Link>
                        <Modal>
                          <Dialog
                            data-testid={`${slugify(
                              itemName.toLowerCase()
                            )}-form-modal`}
                          >
                            {({ close }) => (
                              <CascadiaJsonForm
                                locale={locale}
                                schema={schema}
                                uischema={uiSchemaToUse}
                                initialValues={data[index]}
                                parentValues={data}
                                rootValues={rootValues}
                                rootSchema={rootSchema}
                                renderers={props.renderers}
                                formID={formID}
                                onSubmit={async (values) => {
                                  if (props.onItemChanged && !isSummary) {
                                    const errors = await props.onItemChanged(
                                      data,
                                      values as object,
                                      index
                                    );
                                    if (errors) {
                                      navigateToFirstErrorPage(errors);
                                      throw new CascadiaFormSubmissionError(
                                        errors
                                      );
                                    }
                                  }

                                  // Replace in one update. Removing the row and adding it
                                  // back on a timer lets the parent form keep the shorter
                                  // list, which drops the item.
                                  if (dispatch) {
                                    dispatch(
                                      update(path, (items) => {
                                        if (!Array.isArray(items)) {
                                          return [values];
                                        }
                                        if (
                                          index >= 0 &&
                                          index < items.length
                                        ) {
                                          items[index] = values;
                                          return items;
                                        }
                                        items.push(values);
                                        return items;
                                      })
                                    );
                                  } else {
                                    addItem(path, values)();
                                  }

                                  setCurrentPageIdx(0);
                                  close();
                                }}
                              >
                                <DialogHeader
                                  testID={slugify(itemName.toLowerCase())}
                                  title={t(`Add {{label}}`, {
                                    label: itemName.toLowerCase(),
                                  })}
                                  onClose={() => {
                                    setCurrentPageIdx(0);
                                    close();
                                  }}
                                >
                                  {currentPageIdx > 0 && (
                                    <GhostIconButton
                                      size="small"
                                      icon="arrow_back"
                                      onPress={() =>
                                        setCurrentPageIdx(currentPageIdx - 1)
                                      }
                                    />
                                  )}
                                </DialogHeader>
                                <DialogContent ref={modalContentRef}>
                                  <CascadiaJsonFormContent />
                                </DialogContent>
                                <DialogFooter
                                  leftContent={
                                    <Link
                                      onPress={() => {
                                        if (removeItems) {
                                          removeItems(path, [index])();

                                          if (
                                            props.onItemRemoved &&
                                            !isSummary
                                          ) {
                                            props.onItemRemoved(data, [index]);
                                          }
                                        }
                                        setCurrentPageIdx(0);
                                        close();
                                      }}
                                      color="text-weak"
                                      testID={`${slugify(
                                        itemName.toLowerCase()
                                      )}-delete-button`}
                                    >
                                      <Trans>Delete</Trans>
                                    </Link>
                                  }
                                >
                                  <BasicButton
                                    testID={`${slugify(
                                      itemName.toLowerCase()
                                    )}-cancel-button`}
                                    onPress={() => {
                                      setCurrentPageIdx(0);
                                      close();
                                    }}
                                  >
                                    <Trans>Cancel</Trans>
                                  </BasicButton>
                                  {isMultiPage ? (
                                    <ActionListMultiPageFooterButtons
                                      itemName={itemName}
                                      isMultiPage={isMultiPage}
                                      currentPageIdx={currentPageIdx}
                                      lastPageIdx={lastPageIdx}
                                      isMultiPageWhen={isMultiPageWhen}
                                      isNextPageDisabled={isNextPageDisabled}
                                      setCurrentPageIdx={setCurrentPageIdx}
                                    />
                                  ) : (
                                    <CascadiaJsonFormSubmit
                                      testId={slugify(itemName.toLowerCase())}
                                    >
                                      <Trans>Save</Trans>
                                    </CascadiaJsonFormSubmit>
                                  )}
                                </DialogFooter>
                              </CascadiaJsonForm>
                            )}
                          </Dialog>
                        </Modal>
                      </ModalTrigger>
                    }
                  >
                    {uischema?.options?.hideSubLabel === true
                      ? undefined
                      : props.renderSubtitle
                        ? props.renderSubtitle(item)
                        : subtitles.map((label: string) => {
                            return (
                              <Text
                                key={label}
                                style="global/body-secondary"
                                color="text-weak"
                              >
                                {label}
                              </Text>
                            );
                          })}
                  </ActionListItem>
                  {index < data?.length - 1 && <Divider />}
                </Fragment>
              );
            })}
          </ActionList>
        </div>
      )}
      {(!maxItems || (data?.length ?? 0) < maxItems) &&
        !hasMaximumValidationItems && (
          <ModalTrigger>
            <BasicButton
              testID={`${slugify(itemName.toLowerCase())}-add-button`}
              leftIcon="add"
            >
              {t(`Add {{label}}`, {
                label: itemName.toLowerCase(),
              })}
            </BasicButton>
            <Modal>
              <Dialog>
                {({ close }) => (
                  <CascadiaJsonForm
                    initialValues={{}}
                    schema={schema}
                    uischema={uiSchemaToUse}
                    rootValues={rootValues}
                    rootSchema={rootSchema}
                    renderers={props.renderers}
                    formID={formID}
                    onSubmit={async (values) => {
                      if (props.onItemChanged) {
                        const errors = await props.onItemChanged(
                          data,
                          values as object,
                          data?.length
                        );
                        if (errors) {
                          navigateToFirstErrorPage(errors);
                          throw new CascadiaFormSubmissionError(errors);
                        }
                      }

                      addItem(path, values)();
                      setCurrentPageIdx(0);
                      close();
                    }}
                    parentValues={data}
                    locale={locale}
                  >
                    <DialogHeader
                      title={t(`Add {{label}}`, {
                        label: itemName.toLowerCase(),
                      })}
                      onClose={() => {
                        setCurrentPageIdx(0);
                        close();
                      }}
                    >
                      {currentPageIdx > 0 && (
                        <GhostIconButton
                          size="small"
                          icon="arrow_back"
                          onPress={() => setCurrentPageIdx(currentPageIdx - 1)}
                        />
                      )}
                    </DialogHeader>
                    <DialogContent ref={modalContentRef}>
                      <CascadiaJsonFormContent />
                    </DialogContent>
                    <DialogFooter>
                      <ButtonGroup justify="end">
                        <BasicButton
                          testID={`${slugify(
                            itemName.toLowerCase()
                          )}-cancel-button`}
                          onPress={() => {
                            setCurrentPageIdx(0);
                            close();
                          }}
                        >
                          <Trans>Cancel</Trans>
                        </BasicButton>
                        {isMultiPage ? (
                          <ActionListMultiPageFooterButtons
                            itemName={itemName}
                            isMultiPage={isMultiPage}
                            currentPageIdx={currentPageIdx}
                            lastPageIdx={lastPageIdx}
                            isMultiPageWhen={isMultiPageWhen}
                            isNextPageDisabled={isNextPageDisabled}
                            setCurrentPageIdx={setCurrentPageIdx}
                          />
                        ) : (
                          <CascadiaJsonFormSubmit
                            testId={slugify(itemName.toLowerCase())}
                          >
                            <Trans>Save</Trans>
                          </CascadiaJsonFormSubmit>
                        )}
                      </ButtonGroup>
                    </DialogFooter>
                  </CascadiaJsonForm>
                )}
              </Dialog>
            </Modal>
          </ModalTrigger>
        )}
      {requirementMessages && (
        <RequirementsList
          requirementsList={getRequirementsList(
            requirementMessages,
            errors,
            isSubmitted,
            props.label,
            alwaysDisplayRequirementsAsBullets
          )}
        />
      )}
      {!requirementMessages && isSubmitted && !props.hideFieldErrors && (
        <FieldErrors>{errors}</FieldErrors>
      )}
    </div>
  );
}

export const ActionListControlRenderer = withJsonFormsArrayControlProps(
  withArrayControlVisibility((props) => {
    const {
      schema,
      uischema,
      data,
      path,
      rootSchema,
      uischemas,
      addItem,
      removeItems,
      moveUp,
      moveDown,
      id,
      visible,
      enabled,
      errors,
      translations,
    } = props;

    const controlElement = uischema as ControlElement;
    const labelDescription = Helpers.createLabelDescriptionFrom(
      controlElement,
      schema
    );
    const label = labelDescription.show ? labelDescription.text : "";

    return (
      <ActionListControl
        data={data}
        label={label || "no label"}
        path={path}
        schema={schema}
        errors={errors}
        addItem={addItem}
        removeItems={removeItems}
        moveUp={moveUp}
        moveDown={moveDown}
        uischema={uischema}
        uischemas={uischemas}
        rootSchema={rootSchema}
        id={id}
        visible={visible}
        enabled={enabled}
        translations={translations}
        renderers={props.renderers}
      />
    );
  })
);

const styles = {
  array: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-6)",
  }),
  arrayRenderer: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  element: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  elementHeading: css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  }),
};
